import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Coins, Search } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { setCurrency } from '@/store/currencySlice';
import { CURRENCIES } from '@/utils/currency';
import './CurrencyChooserModal.scss';

export const CurrencyChooserModal = ({ open, onOpenChange }) => {
  const dispatch = useDispatch();
  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const currencies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return CURRENCIES;
    return CURRENCIES.filter(({ code, name, symbol }) => [code, name, symbol]
      .some((value) => value.toLowerCase().includes(normalizedQuery)));
  }, [query]);

  const selectCurrency = (code) => {
    dispatch(setCurrency(code));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="currency-chooser">
        <DialogHeader className="currency-chooser__header">
          <p className="currency-chooser__eyebrow"><Coins size={16} aria-hidden="true" /> Currency</p>
          <DialogTitle>Choose a currency</DialogTitle>
          <DialogDescription>Prices refresh in your selected display currency.</DialogDescription>
        </DialogHeader>
        <label className="currency-chooser__search">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Search supported currencies</span>
          <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name, code, or symbol" />
        </label>
        <ul className="currency-chooser__grid" aria-label="Supported currencies">
          {currencies.map((currency) => {
            const selected = selectedCurrency === currency.code;
            return <li key={currency.code}><button type="button" className={`currency-chooser__option${selected ? ' currency-chooser__option--selected' : ''}`} onClick={() => selectCurrency(currency.code)} aria-pressed={selected}>
              <span className="currency-chooser__symbol" dir="auto">{currency.symbol}</span>
              <span className="currency-chooser__details"><strong>{currency.code}</strong><span>{currency.name}</span></span>
              {selected && <Check className="currency-chooser__check" size={18} aria-label="Selected" />}
            </button></li>;
          })}
        </ul>
        {!currencies.length && <p className="currency-chooser__empty">No supported currency matches “{query}”.</p>}
      </DialogContent>
    </Dialog>
  );
};